/**
 * Cleanup Legacy Storage
 *
 * This utility cleans up old localStorage keys that don't have environment prefixes.
 * This is a one-time cleanup script that should be run after deploying the environment
 * isolation changes.
 *
 * Usage:
 *   Import and call cleanupLegacyStorage() once on app initialization
 */

import { CODE_VERIFIER_KEY } from "@/lib/storage";
import { envStorage } from "@/lib/storage/envIsolatedStorage";
import { Logger } from "@/utils/logger";
import { purgePersistedPersonalData } from "@/utils/purgePersistedPersonalData";

// List of legacy keys that need to be cleaned up
const LEGACY_KEYS = [
  "i18nextLng",
  "inventory-store",
  "sidebar_open_keys",
  "master-storage",
  "__legacy_storage_cleaned__", // Old cleanup flag without environment prefix
  // Legacy Zustand stores (before environment isolation)
  "user-store",
  "user-additional-store",
  "clinic-store",
  "patient-store",
  "doctor-store",
  "visit-storage",
  "vital-signs-storage",
  "echo-consultation-storage",
  "echo-common-storage",
  "user", // User data (moved to sessionStorage)
  // Add any other legacy keys discovered
] as const;

// Valid environment prefixes
const VALID_ENV_PREFIXES = ["development", "test", "staging", "production"] as const;

// Cleanup flag key (with environment isolation)
const CLEANUP_FLAG_KEY = "__legacy_storage_cleaned__";

/**
 * Check if a key has a valid environment prefix
 */
function hasEnvironmentPrefix(key: string): boolean {
  return VALID_ENV_PREFIXES.some((prefix) => key.startsWith(`${prefix}-`));
}

/**
 * Clean up legacy localStorage keys that don't have environment prefixes
 *
 * This function:
 * 1. Removes known legacy keys
 * 2. Scans for any other keys without environment prefixes
 * 3. Logs the cleanup results
 *
 * @returns Number of keys cleaned up
 */
export function cleanupLegacyStorage(): number {
  // Runs on every boot, independent of the one-time legacy-key cleanup flag below.
  purgePersistedPersonalData({
    store: envStorage,
    rawStore: localStorage,
    extraKeys: [CODE_VERIFIER_KEY],
    onError: (message, error) => Logger.error(`[PurgePersistedPersonalData] ${message}`, error),
  });

  // Check if cleanup has already been performed (using environment-isolated storage)
  if (envStorage.getItem(CLEANUP_FLAG_KEY) === "true") {
    Logger.info("[CleanupLegacyStorage] Cleanup already performed, skipping.");
    return 0;
  }

  let cleanedCount = 0;
  const keysToRemove: string[] = [];

  // Step 1: Remove known legacy keys
  for (const legacyKey of LEGACY_KEYS) {
    if (localStorage.getItem(legacyKey) !== null) {
      keysToRemove.push(legacyKey);
    }
  }

  // Step 2: Scan for other keys without environment prefixes
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    // Skip keys that already have environment prefixes
    // (Our cleanup flag now has environment prefix, so it's automatically skipped)
    if (hasEnvironmentPrefix(key)) continue;

    // This is a legacy key without environment prefix
    if (!keysToRemove.includes(key)) {
      keysToRemove.push(key);
    }
  }

  // Step 3: Remove all identified legacy keys
  for (const key of keysToRemove) {
    try {
      localStorage.removeItem(key);
      cleanedCount++;
      Logger.info(`[CleanupLegacyStorage] Removed legacy key: ${key}`);
    } catch (error) {
      Logger.error(`[CleanupLegacyStorage] Failed to remove key: ${key}`, error);
    }
  }

  // Mark cleanup as completed (using environment-isolated storage)
  envStorage.setItem(CLEANUP_FLAG_KEY, "true");

  if (cleanedCount > 0) {
    Logger.info(`[CleanupLegacyStorage] Cleanup completed. Removed ${cleanedCount} legacy key(s).`);
    Logger.info(`[CleanupLegacyStorage] Removed keys: ${keysToRemove.join(", ")}`);
  } else {
    Logger.info("[CleanupLegacyStorage] No legacy keys found.");
  }

  return cleanedCount;
}

/**
 * Force cleanup by removing the cleanup flag
 * Use this if you need to re-run the cleanup
 *
 * Usage in browser console:
 *   window.__forceCleanupLegacyStorage()
 */
export function forceCleanupLegacyStorage(): number {
  envStorage.removeItem(CLEANUP_FLAG_KEY);
  return cleanupLegacyStorage();
}

// Expose to window for manual cleanup in browser console
if (typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).__forceCleanupLegacyStorage =
    forceCleanupLegacyStorage;
  (window as unknown as Record<string, unknown>).__getLegacyKeys = getLegacyKeys;
}

/**
 * Check if cleanup has been performed
 */
export function isLegacyStorageCleaned(): boolean {
  return envStorage.getItem(CLEANUP_FLAG_KEY) === "true";
}

/**
 * Get list of remaining legacy keys (for debugging)
 */
export function getLegacyKeys(): string[] {
  const legacyKeys: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    // Skip keys that already have environment prefixes
    // (Note: our cleanup flag now has environment prefix, so it won't be detected as legacy)
    if (hasEnvironmentPrefix(key)) continue;

    legacyKeys.push(key);
  }

  return legacyKeys;
}
